// override fetch on client-side to enhance fetch responses
// with parsed response data ready to be used in datadog beforeSend
import { isObj } from "@/utils";
import { isCriticalWorkflow } from "@/utils/observability/criticalWorkflows";
import "@/utils/observability/datadog";
import { Datadog } from "@/utils/observability/datadog";
import {
  DatadogAdditionalData,
  extractFetchData,
} from "@/utils/observability/extractFetchData";
import {
  isBackendEndpoint,
  isGraphqlEndpoint,
} from "@/utils/observability/util";

declare global {
  // expand fetch response with additional data for Datadog RUM
  interface Response {
    datadogAdditionalData?: DatadogAdditionalData;
  }
}

const BAGGAGE_HEADER = "baggage";

export function overrideFetch() {
  if (typeof window !== "undefined") {
    const originalFetch = window.fetch;
    window.fetch = async (input, init) => {
      try {
        if (isGraphqlEndpoint(input) || isBackendEndpoint(input)) {
          addTracingContext(init?.headers);
        }
        const response = await originalFetch(input, init);
        response.datadogAdditionalData = await extractFetchData(input, init, {
          response,
        });

        return response;
      } catch (error: unknown) {
        if (isObj(error) && error !== null) {
          error.datadogAdditionalData = await extractFetchData(input, init, {
            error,
          });
        }
        throw error;
      }
    };
  }
}

function addTracingContext(headers: HeadersInit | undefined) {
  if (typeof headers === "undefined") {
    return;
  }
  if (isCriticalWorkflow()) {
    addBaggage(headers, "critical_workflow", "true");
  }
  if (typeof Datadog.getMedspaId() === "string") {
    addBaggage(headers, "medspa.id", Datadog.getMedspaId());
  }
  addBaggage(headers, "view.url_details.path", window.location.pathname);
}

/**
 * Append key=value pair to the baggage header.
 * Reference: https://www.w3.org/TR/baggage/
 */
function addBaggage(headers: HeadersInit, key: string, value: string) {
  const baggage = `${key}=${value}`;
  if (headers instanceof Headers) {
    headers.append(BAGGAGE_HEADER, baggage);
  } else if (Array.isArray(headers)) {
    headers.push([BAGGAGE_HEADER, baggage]);
  } else if (typeof headers === "object") {
    append(headers, BAGGAGE_HEADER, baggage);
  }
}

/**
 * Appends the provided value to the specified header.
 * If the header already exists in the headers object, the new value is appended to the existing value with a comma separator.
 * If the header does not exist, it is added with the provided value.
 */
function append(headers: Record<string, string>, name: string, value: string) {
  if (headers[name]) {
    headers[name] += `,${value}`;
  } else {
    headers[name] = value;
  }
}
